import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Minus, Plus } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch
} from '@bliks-io/ui';

export function InfoButton({ text }: { text: string }) {
  const { t } = useTranslation();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={t('partner.calculator.info')}>
          <Info className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <p className="text-sm text-muted-foreground">{text}</p>
      </PopoverContent>
    </Popover>
  );
}

interface QuantityFieldProps {
  label: string;
  hint?: string;
  info?: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}

export function QuantityField({
  label,
  hint,
  info,
  value,
  min,
  max,
  onChange
}: QuantityFieldProps) {
  const { t } = useTranslation();
  const id = useId();
  const clamp = (next: number) => Math.min(max, Math.max(min, Math.round(next)));

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-1">
        <Label htmlFor={id}>
          {label}
          {hint && <span className="block text-xs font-normal text-muted-foreground">{hint}</span>}
        </Label>
        {info && <InfoButton text={info} />}
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="icon-sm"
          aria-label={t('partner.calculator.decrease')}
          disabled={value <= min}
          onClick={() => onChange(clamp(value - 1))}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <Input
          id={id}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          value={value}
          className="w-24"
          onChange={(event) => {
            const parsed = Number(event.target.value);
            if (Number.isFinite(parsed) && event.target.value !== '') onChange(clamp(parsed));
          }}
        />
        <Button
          variant="outline"
          size="icon-sm"
          aria-label={t('partner.calculator.increase')}
          disabled={value >= max}
          onClick={() => onChange(clamp(value + 1))}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

interface ToggleRowProps {
  label: string;
  info?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  price: string;
  meta?: string;
  children?: React.ReactNode;
}

export function ToggleRow({
  label,
  info,
  checked,
  onCheckedChange,
  price,
  meta,
  children
}: ToggleRowProps) {
  const id = useId();
  return (
    <div className="flex flex-col gap-3 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
          <Label htmlFor={id}>
            <span className={checked ? undefined : 'text-muted-foreground'}>{label}</span>
          </Label>
          {info && <InfoButton text={info} />}
          {checked && meta && <span className="text-xs text-muted-foreground">{meta}</span>}
        </div>
        <span className="shrink-0 text-sm font-semibold tabular-nums">{checked ? price : '—'}</span>
      </div>
      {checked && children && <div className="pl-12">{children}</div>}
    </div>
  );
}
